import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import SiderProjectList from "./SiderProjectList";
import LoggedInUser from "./LoggedInUser";
import { fetchUnreadCounts } from "../api/services";
import { initSocket } from "../socket";
import { useSelector } from "react-redux";
import { showNotification } from "../utils/chatNotificationHelper";
const Sidebar = ({ setActivePage, onMenuSelect }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [unreadCounts, setUnreadCounts] = useState(0);
  const token = localStorage.getItem("token");
  const loggedInUserId = localStorage.getItem("id");
  const user = JSON.parse(localStorage.getItem("user") || "null");

  const socket = initSocket(token);

  useEffect(() => {
    const path = location.pathname.replace("/", "") || "/";
    setActivePage(path);
  }, [location, setActivePage]);

  const getIcon = (path, icon, activeIcon) => {
    return location.pathname === path ? activeIcon : icon;
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768); // Mobile breakpoint
    };

    handleResize(); // Check on component mount
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!user && !socket) return;

    fetchUnreadCounts(loggedInUserId)
      .then((result) => {
        if (Array.isArray(result)) {
          const total = result.reduce(
            (sum, item) => sum + (item.count || 0),
            0,
          );
          setUnreadCounts(total);
        } else {
          setUnreadCounts(0);
        }
      })
      .catch((err) => console.error("fetchUnreadCounts error", err));

    socket.emit("join", loggedInUserId);

    const handleUnreadCounts = (counts) => {
      const total = Array.isArray(counts)
        ? counts.reduce((sum, item) => sum + (item.count || 0), 0)
        : 0;

      // 🔔 Show toast + sound when count increases
      if (total > unreadCounts) {
        showNotification("New message received!");
      }

      setUnreadCounts(total);
    };

    socket.on("unreadCounts", handleUnreadCounts);

    return () => {
      socket.off("unreadCounts", handleUnreadCounts);
    };
  }, [user, token, socket, unreadCounts, loggedInUserId]);

  useEffect(() => {
    if (loggedInUserId) {
      fetchUnreadCounts(loggedInUserId).then((data) => {
        setUnreadCounts(data?.unreadCounts);
      });

      socket.on("unreadCounts", (data) => {
        setUnreadCounts(data?.unreadCounts);
      });
    }
  }, [loggedInUserId]);

  return (
    <div
      className={`bg-gradient-to-b from-white via-gray-50 to-white ${
        isMobile ? "h-[92%]" : "h-full"
      } w-[250px] fixed font-customFont border-r border-gray-200`}
    >
      <div className="flex items-center justify-start ms-3 mt-3">
        <img src="/expensifyLogo.png" className="w-[35px]" alt="Logo" />
        <p
          className="text-black font-bold text-2xl"
          style={{ fontFamily: "Noto Serif, sans-serif" }}
        >
          Expensify
        </p>
      </div>

      <div
        className={`mt-2 ms-3 pb-2 space-y-1 border-b-2 border-[#D5D7DA] ${
          isMobile ? "me-3" : "me-3"
        } `}
      >
        <div
          className={`flex items-center p-2 rounded-lg cursor-pointer transition-all duration-300 ${
            location.pathname === "/dashboard"
              ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/25"
              : "text-gray-600 hover:bg-blue-50 hover:text-blue-600"
          }`}
          onClick={() => {
            navigate("/dashboard");
            if (onMenuSelect) onMenuSelect();
          }}
        >
          <div
            className={`w-7 h-7 rounded-lg flex items-center justify-center mr-3 transition-all duration-300 ${
              location.pathname === "/dashboard"
                ? "bg-white/20"
                : "bg-gray-100 group-hover:bg-blue-100"
            }`}
          >
            <i className="fas fa-tachometer-alt text-sm"></i>
          </div>
          <span className="flex-1">Dashboard</span>
          {location.pathname === "/dashboard" && (
            <div className="w-2 h-2 bg-white rounded-full"></div>
          )}
        </div>
        <div
          className={`flex items-center p-2 rounded-lg cursor-pointer transition-all duration-300 ${
            location.pathname === "/expenseList"
              ? "bg-gradient-to-r from-teal-500 to-teal-600 text-white shadow-lg shadow-teal-500/25"
              : "text-gray-600 hover:bg-teal-50 hover:text-teal-700"
          }`}
          onClick={() => {
            navigate("/expenseList");
            if (onMenuSelect) onMenuSelect();
          }}
        >
          <div
            className={`w-7 h-7 rounded-lg flex items-center justify-center mr-3 transition-all duration-300 ${
              location.pathname === "/expenseList"
                ? "bg-white/20"
                : "bg-gray-100 group-hover:bg-teal-100"
            }`}
          >
            <i className="fas fa-credit-card text-sm"></i>
          </div>
          <span className="flex-1">Transactions</span>
          {location.pathname === "/expenseList" && (
            <div className="w-2 h-2 bg-white rounded-full"></div>
          )}
        </div>

        <div
          className={`flex items-center p-2 rounded-lg cursor-pointer transition-all duration-300 ${
            location.pathname === "/projects"
              ? "bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-lg shadow-purple-500/25"
              : "text-gray-600 hover:bg-purple-50 hover:text-purple-600"
          }`}
          onClick={() => {
            navigate("/projects");
            if (onMenuSelect) onMenuSelect();
          }}
        >
          <div
            className={`w-7 h-7 rounded-lg flex items-center justify-center mr-3 transition-all duration-300 ${
              location.pathname === "/projects"
                ? "bg-white/20"
                : "bg-gray-100 group-hover:bg-purple-100"
            }`}
          >
            <i className="fas fa-folder-open text-sm"></i>
          </div>
          <span className="flex-1">Projects</span>
          {location.pathname === "/projects" && (
            <div className="w-2 h-2 bg-white rounded-full"></div>
          )}
        </div>
        <div
          className={`flex items-center p-2 rounded-xl cursor-pointer transition-all duration-300 ${
            location.pathname === "/invoices"
              ? "bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg shadow-green-500/25"
              : "text-gray-600 hover:bg-green-50 hover:text-green-600"
          }`}
          onClick={() => {
            navigate("/invoices");
            if (onMenuSelect) onMenuSelect();
          }}
        >
          <div
            className={`w-7 h-7 rounded-lg flex items-center justify-center mr-3 transition-all duration-300 ${
              location.pathname === "/invoices"
                ? "bg-white/20"
                : "bg-gray-100 group-hover:bg-red-100"
            }`}
          >
            <i className="fas fa-file-invoice-dollar text-sm"></i>
          </div>
          <span className="flex-1">Invoices</span>
          {location.pathname === "/invoices" && (
            <div className="w-2 h-2 bg-white rounded-full"></div>
          )}
        </div>
        <div
          className={`flex items-center p-2 rounded-xl cursor-pointer transition-all duration-300 ${
            location.pathname === "/team"
              ? "bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg shadow-orange-500/25"
              : "text-gray-600 hover:bg-orange-50 hover:text-orange-600"
          }`}
          onClick={() => {
            navigate("/team");
            if (onMenuSelect) onMenuSelect();
          }}
        >
          <div
            className={`w-7 h-7 rounded-lg flex items-center justify-center mr-3 transition-all duration-300 ${
              location.pathname === "/team"
                ? "bg-white/20"
                : "bg-gray-100 group-hover:bg-orange-100"
            }`}
          >
            <i className="fas fa-user-friends text-sm"></i>
          </div>
          <span className="flex-1">Team</span>
          {location.pathname === "/team" && (
            <div className="w-2 h-2 bg-white rounded-full"></div>
          )}
        </div>
        <div
          className={`flex items-center p-2 rounded-lg cursor-pointer transition-all duration-300 ${
            location.pathname === "/chats"
              ? "bg-gradient-to-r from-indigo-500 to-indigo-600 text-white shadow-lg shadow-indigo-500/25"
              : "text-gray-600 hover:bg-indigo-50 hover:text-indigo-600"
          }`}
          onClick={() => {
            navigate("/chats");
            if (onMenuSelect) onMenuSelect();
          }}
        >
          <div
            className={`w-7 h-7 rounded-lg flex items-center justify-center mr-3 transition-all duration-300 ${
              location.pathname === "/chats"
                ? "bg-white/20"
                : "bg-gray-100 group-hover:bg-indigo-100"
            }`}
          >
            <i className="fas fa-comment-dots text-sm"></i>
          </div>
          <span className="flex-1">Chat</span>
          {unreadCounts > 0 && (
            <span className="bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold shadow-lg">
              {unreadCounts}
            </span>
          )}
          {location.pathname === "/chats" && !unreadCounts && (
            <div className="w-2 h-2 bg-white rounded-full"></div>
          )}
        </div>
      </div>

      <SiderProjectList onMenuSelect={onMenuSelect} />

      <div className="absolute bottom-0 left-0 w-full p-3 flex items-center cursor-pointer text-gray-700">
        <LoggedInUser onMenuSelect={onMenuSelect} />
      </div>
    </div>
  );
};

Sidebar.propTypes = {
  setActivePage: PropTypes.func.isRequired,
};

export default Sidebar;
